import React, { useState } from 'react';

import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { useAuth } from 'context/auth-context';
import usePaymentInfoRequestCandidates from 'hooks/athlete/usePaymentInfoRequestCandidates';
import useWaiverCandidates from 'hooks/athlete/useWaiverCandidates';

import AthletesPageHeader from './components/AthletesPageHeader';
import AthletesTableContainer from './components/AthletesTableContainer';
import DrawerAthleteForm from './components/DrawerAthleteForm';
import PaymentInfoRequestBanner from './components/PaymentInfoRequestBanner';
import SendPaymentInfoRequestModal from './components/SendPaymentInfoRequestModal';
import SendWaiverBanner from './components/SendWaiverBanner';
import SendWaiverModal from './components/SendWaiverModal';

interface FilterTerms {
  searchTerm: string;
  isArrangeByLastName: boolean;
  athleteStatus: string;
  loginEnabled: string;
  packages: string[];
  hasPaymentMethod: string;
  hasSignedWaiver: string;
  glmLinkState: string;
}

const Athletes = (): JSX.Element => {
  const { hasPerm } = useAuth();

  const athletesPerm = hasPerm('AthleteMutate');
  const canSendWaivers = hasPerm('IssuedDocumentMutate');

  const initialSearchFiltersValue = {
    searchTerm: '',
    isArrangeByLastName: false,
    athleteStatus: '',
    loginEnabled: '',
    packages: [],
    hasPaymentMethod: '',
    hasSignedWaiver: '',
    glmLinkState: '',
  };

  const [isCreateFormOpened, { open: openCreateForm, close: closeCreateForm }] = useDisclosure(false);
  const [isEditFormOpened, { open: openEditForm, close: closeEditForm }] = useDisclosure(false);
  const [
    isCreateLinkedProfileFormOpened,
    { open: openCreateLinkedProfileForm, close: closeCreateLinkedProfileForm },
  ] = useDisclosure(false);
  const [
    isCreateDependentProfileFormOpened,
    { open: openCreateDependentProfileForm, close: closeCreateDependentProfileForm },
  ] = useDisclosure(false);
  const [isPaymentRequestModalOpen, { open: openPaymentRequestModal, close: closePaymentRequestModal }] =
    useDisclosure(false);
  const [isSendWaiverModalOpen, { open: openSendWaiverModal, close: closeSendWaiverModal }] =
    useDisclosure(false);

  const [searchFilters, setSearchFilters] = useState<FilterTerms>(initialSearchFiltersValue);
  const [orderingValue, setOrderingValue] = useState<string>('firstName');
  const [debouncedSearchFilters] = useDebouncedValue<FilterTerms>(searchFilters, 250);
  const [athleteId, setAthleteId] = useState<string>('');

  const isNoPaymentMethodFilterActive = debouncedSearchFilters.hasPaymentMethod === 'false';
  const { data: paymentRequestCandidates = [] } = usePaymentInfoRequestCandidates(debouncedSearchFilters, {
    enabled: isNoPaymentMethodFilterActive,
  });

  const isNoWaiverFilterActive = canSendWaivers && debouncedSearchFilters.hasSignedWaiver === 'false';
  const { data: waiverCandidates } = useWaiverCandidates(debouncedSearchFilters, {
    enabled: isNoWaiverFilterActive,
  });

  const handleCloseAndResetForm = (): void => {
    setAthleteId('');
    closeCreateForm();
    closeEditForm();
    closeCreateLinkedProfileForm();
    closeCreateDependentProfileForm();
  };

  const handleEditAthlete = (id: string): void => {
    setAthleteId(id);
    openEditForm();
  };

  const handleCreateLinkedProfile = (id: string): void => {
    setAthleteId(id);
    openCreateLinkedProfileForm();
  };

  const handleCreateDependentProfile = (id: string): void => {
    setAthleteId(id);
    openCreateDependentProfileForm();
  };

  return (
    <>
      <AthletesPageHeader
        searchFilters={searchFilters}
        orderingValue={orderingValue}
        setSearchFilters={setSearchFilters}
        initialSearchFiltersValue={initialSearchFiltersValue}
        handleOpenCreateForm={openCreateForm}
        permission={athletesPerm}
      />

      {isNoPaymentMethodFilterActive && (
        <PaymentInfoRequestBanner
          count={paymentRequestCandidates.length}
          onReview={openPaymentRequestModal}
        />
      )}

      {isNoWaiverFilterActive && waiverCandidates && (
        <SendWaiverBanner count={waiverCandidates.length} onReview={openSendWaiverModal} />
      )}

      <AthletesTableContainer
        searchFilters={debouncedSearchFilters}
        orderingValue={orderingValue}
        setOrderingValue={setOrderingValue}
        handleEditAthlete={handleEditAthlete}
        handleCreateLinkedProfile={handleCreateLinkedProfile}
        handleCreateDependentProfile={handleCreateDependentProfile}
        permission={athletesPerm}
      />

      <DrawerAthleteForm
        type={
          isCreateFormOpened
            ? 'create'
            : isEditFormOpened
            ? 'edit'
            : isCreateLinkedProfileFormOpened
            ? 'create-linked'
            : isCreateDependentProfileFormOpened
            ? 'create-dependent'
            : 'closed'
        }
        opened={
          isCreateFormOpened ||
          isEditFormOpened ||
          isCreateLinkedProfileFormOpened ||
          isCreateDependentProfileFormOpened
        }
        closeHandler={handleCloseAndResetForm}
        athleteId={isEditFormOpened ? athleteId : undefined}
        primaryAthleteId={
          isCreateLinkedProfileFormOpened || isCreateDependentProfileFormOpened ? athleteId : undefined
        }
      />

      <SendPaymentInfoRequestModal
        opened={isPaymentRequestModalOpen}
        onClose={closePaymentRequestModal}
        filters={debouncedSearchFilters}
      />

      <SendWaiverModal
        opened={isSendWaiverModalOpen}
        onClose={closeSendWaiverModal}
        filters={debouncedSearchFilters}
      />
    </>
  );
};

export default Athletes;
